import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { t } from '@/shared/lib/i18n'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'

export const US_PHONE_CODE = '+1'
// E.164 national numbers stay within 15 digits. Input is capped here; validity comes from libphonenumber.
export const PHONE_NUMBER_MAX_DIGITS = 15

export interface SiteFormValidationData {
  name: string
  address: string
  city: string
  state: string
  zip: string
  phoneNumber: string
  followParentSchedule: boolean
  customDaySchedule: WeekdaySchedule
  timezoneId: number | null
  countryId: number | null
  phoneCode: string
}

export type SiteFormClientErrors = Partial<Record<keyof SiteFormValidationData, string>>

const phoneDigits = (rawValue: string): string => rawValue.replace(/\D/g, '')

export const sanitizePhoneNumberInput = (rawValue: string): string =>
  phoneDigits(rawValue).slice(0, PHONE_NUMBER_MAX_DIGITS)

// Default libphonenumber-js metadata only checks length. /max matches Phonelib's strict digit rules.
export const phoneNumberFormatError = (phoneCode: string, phoneNumber: string): string | null => {
  const normalizedCode = phoneCode.trim()
  const digits = phoneDigits(phoneNumber)
  if (!normalizedCode && !digits) return null

  const invalidForCode = t('organization.sites.form.phoneNumberInvalidForCode')
  if (!normalizedCode || !digits) return invalidForCode

  const callingCode = normalizedCode.startsWith('+') ? normalizedCode : `+${normalizedCode}`
  const parsed = parsePhoneNumberFromString(`${callingCode}${digits}`)
  if (parsed?.isValid()) return null

  return invalidForCode
}

export const trimSiteFormData = (data: SiteFormValidationData): SiteFormValidationData => ({
  ...data,
  name: data.name.trim(),
  address: data.address.trim(),
  city: data.city.trim(),
  state: data.state.trim(),
  zip: data.zip.trim(),
  phoneNumber: phoneDigits(data.phoneNumber),
  phoneCode: data.phoneCode.trim(),
})

export const buildClientErrors = (data: SiteFormValidationData): SiteFormClientErrors => {
  const trimmed = trimSiteFormData(data)
  const clientErrors: SiteFormClientErrors = {}

  if (!trimmed.name) {
    clientErrors.name = t('organization.sites.form.nameRequired')
  }
  if (!trimmed.address) {
    clientErrors.address = t('organization.sites.form.addressRequired')
  }
  if (!trimmed.city) {
    clientErrors.city = t('organization.sites.form.cityRequired')
  }
  if (!trimmed.state) {
    clientErrors.state = t('organization.sites.form.stateRequired')
  }
  if (!trimmed.zip) {
    clientErrors.zip = t('organization.sites.form.zipRequired')
  }
  // Phone is optional (REBUILD-108). When either side is present, the number must be valid for that code.
  const formatError = phoneNumberFormatError(trimmed.phoneCode, trimmed.phoneNumber)
  if (formatError) {
    clientErrors.phoneNumber = formatError
  }
  if (!trimmed.timezoneId) {
    clientErrors.timezoneId = t('organization.sites.form.timezoneRequired')
  }
  if (!trimmed.countryId) {
    clientErrors.countryId = t('organization.sites.form.countryRequired')
  }

  return clientErrors
}

export const firstErrorFieldId = (clientErrors: SiteFormClientErrors): string | null => {
  const fieldOrder: Array<keyof SiteFormValidationData> = [
    'name',
    'address',
    'city',
    'state',
    'zip',
    'phoneCode',
    'phoneNumber',
    'timezoneId',
    'countryId',
  ]

  return fieldOrder.find((field) => clientErrors[field]) ?? null
}
